/**
 * Performance monitoring and optimization utilities
 * Tracks Core Web Vitals, bundle size, and runtime performance
 */

import { captureMessage } from './sentry';

// Core Web Vitals thresholds (Google recommended)
const CWV_THRESHOLDS = {
  LCP: { good: 2500, needsImprovement: 4000 }, // Largest Contentful Paint
  FID: { good: 100, needsImprovement: 300 },  // First Input Delay
  CLS: { good: 0.1, needsImprovement: 0.25 }, // Cumulative Layout Shift
  FCP: { good: 1800, needsImprovement: 3000 }, // First Contentful Paint
  TTFB: { good: 800, needsImprovement: 1800 }, // Time to First Byte
};

// Performance metric data type
interface MetricData {
  timestamp: number;
  value?: number;
  [key: string]: unknown;
}

// Performance metrics storage
class PerformanceMonitor {
  private metrics: Map<string, MetricData[]> = new Map();
  private observers: PerformanceObserver[] = [];

  constructor() {
    this.initCoreWebVitals();
    this.initResourceMonitoring();
    this.initNavigationTiming();
  }

  private initCoreWebVitals() {
    // Largest Contentful Paint
    if ('PerformanceObserver' in window) {
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1] as PerformanceEntry & { 
            element?: Element; 
            url?: string; 
            size?: number; 
          };

          this.recordMetric('LCP', {
            value: lastEntry.startTime,
            element: lastEntry.element?.tagName,
            url: lastEntry.url,
            size: lastEntry.size,
          });

          this.evaluateCWV('LCP', lastEntry.startTime);
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        this.observers.push(lcpObserver);

        // First Input Delay
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            const fidEntry = entry as PerformanceEventTiming;
            this.recordMetric('FID', {
              value: fidEntry.processingStart - fidEntry.startTime,
              eventType: fidEntry.name,
              target: (fidEntry as unknown as { target?: Element }).target?.tagName,
            });

            this.evaluateCWV('FID', fidEntry.processingStart - fidEntry.startTime);
          });
        });
        fidObserver.observe({ entryTypes: ['first-input'] });
        this.observers.push(fidObserver);

        // Cumulative Layout Shift
        const clsObserver = new PerformanceObserver((list) => {
          let clsValue = 0;
          const entries = list.getEntries() as (PerformanceEntry & { hadRecentInput?: boolean; value?: number })[];

          entries.forEach((entry) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value || 0;
            }
          });

          this.recordMetric('CLS', { value: clsValue });
          this.evaluateCWV('CLS', clsValue);
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });
        this.observers.push(clsObserver);

      } catch (error) {
        console.warn('Performance monitoring not fully supported:', error);
      }
    }
  }

  private initResourceMonitoring() {
    // Monitor resource loading performance
    if ('PerformanceObserver' in window) {
      try {
        const resourceObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries() as PerformanceResourceTiming[];
          entries.forEach((entry) => {
            if (entry.duration > 1000) { // Log slow resources (>1s)
              this.recordMetric('slow_resource', {
                name: entry.name,
                duration: entry.duration,
                size: entry.transferSize,
                type: entry.initiatorType,
              });
            }
          });
        });
        resourceObserver.observe({ entryTypes: ['resource'] });
        this.observers.push(resourceObserver);
      } catch (error) {
        console.warn('Resource monitoring not supported:', error);
      }
    }
  }

  private initNavigationTiming() {
    // Monitor navigation timing
    window.addEventListener('load', () => {
      setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        if (navigation) {
          this.recordMetric('navigation', {
            domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
            loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
            totalTime: navigation.loadEventEnd - navigation.fetchStart,
            ttfb: navigation.responseStart - navigation.requestStart,
          });

          this.evaluateCWV('TTFB', navigation.responseStart - navigation.requestStart);
        }
      }, 0);
    });
  }

  private recordMetric(name: string, data: Omit<MetricData, 'timestamp'>) {
    if (!this.metrics.has(name)) {
      this.metrics.set(name, []);
    }
    this.metrics.get(name)!.push({
      timestamp: Date.now(),
      ...data,
    });

    // Keep only last 100 entries per metric
    const entries = this.metrics.get(name)!;
    if (entries.length > 100) {
      entries.splice(0, entries.length - 100);
    }
  }

  private evaluateCWV(metric: keyof typeof CWV_THRESHOLDS, value: number) {
    const thresholds = CWV_THRESHOLDS[metric];
    let rating: 'good' | 'needs-improvement' | 'poor';

    if (value <= thresholds.good) {
      rating = 'good';
    } else if (value <= thresholds.needsImprovement) {
      rating = 'needs-improvement';
    } else {
      rating = 'poor';
    }

    // Log poor performance in production
    if (rating === 'poor' && import.meta.env.VITE_APP_ENV === 'production') {
      captureMessage(`Poor ${metric} performance: ${value}`, 'warning');
    }

    this.recordMetric(`${metric}_rating`, { value, rating });
  }

  // Public API
  getMetrics(name?: string) {
    if (name) {
      return this.metrics.get(name) || [];
    }
    return Object.fromEntries(this.metrics);
  }

  getAverageMetric(name: string): number | null {
    const entries = this.metrics.get(name);
    if (!entries || entries.length === 0) return null;

    const sum = entries.reduce((acc, entry) => acc + entry.value, 0);
    return sum / entries.length;
  }

  // Memory usage monitoring
  getMemoryUsage() {
    // Chrome-specific performance.memory API
    interface PerformanceMemory {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    }
    
    const perf = performance as Performance & { memory?: PerformanceMemory };
    if (perf.memory) {
      const mem = perf.memory;
      return {
        used: mem.usedJSHeapSize,
        total: mem.totalJSHeapSize,
        limit: mem.jsHeapSizeLimit,
        usedPercent: (mem.usedJSHeapSize / mem.jsHeapSizeLimit) * 100,
      };
    }
    return null;
  }

  // Bundle size monitoring (call after app loads)
  monitorBundleSize() {
    // This would be populated by build tools
    interface WindowWithBundleSize extends Window {
      __AEGIS_BUNDLE_SIZE__?: Record<string, number>;
    }
    
    const bundleSize = (window as WindowWithBundleSize).__AEGIS_BUNDLE_SIZE__;
    if (bundleSize) {
      this.recordMetric('bundle_size', bundleSize);

      // Alert if bundle is too large
      const totalSize = Object.values(bundleSize).reduce((a, b) => a + b, 0);
      if (totalSize > 2 * 1024 * 1024) { // 2MB
        captureMessage(`Large bundle size: ${(totalSize / 1024 / 1024).toFixed(2)}MB`, 'warning');
      }
    }
  }

  // Cleanup
  destroy() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    this.metrics.clear();
  }
}

// Singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Web Vitals loader (for modern browsers - web-vitals v4+)
export const loadWebVitals = async () => {
  if (!('PerformanceObserver' in window)) {
    try {
      // web-vitals v4+ uses onCLS, onFID, onFCP, onLCP, onTTFB
      const webVitals = await import('web-vitals');

      webVitals.onCLS((metric) => {
        performanceMonitor['evaluateCWV']('CLS', metric.value);
      });

      // FID is deprecated in web-vitals v4, use INP instead
      if ('onINP' in webVitals) {
        webVitals.onINP((metric) => {
          performanceMonitor['evaluateCWV']('FID', metric.value);
        });
      }

      webVitals.onFCP((metric) => {
        performanceMonitor['evaluateCWV']('FCP', metric.value);
      });

      webVitals.onLCP((metric) => {
        performanceMonitor['evaluateCWV']('LCP', metric.value);
      });

      webVitals.onTTFB((metric) => {
        performanceMonitor['evaluateCWV']('TTFB', metric.value);
      });
    } catch (error) {
      console.warn('Web Vitals failed to load:', error);
    }
  }
};

// Initialize performance monitoring
export const initPerformanceMonitoring = () => {
  // Load Web Vitals polyfill for older browsers
  loadWebVitals();

  // Monitor memory usage every 30 seconds
  if (import.meta.env.VITE_APP_ENV === 'production') {
    setInterval(() => {
      const memory = performanceMonitor.getMemoryUsage();
      if (memory && memory.usedPercent > 80) {
        captureMessage(`High memory usage: ${memory.usedPercent.toFixed(1)}%`, 'warning');
      }
    }, 30000);
  }

  // Monitor bundle size after load
  window.addEventListener('load', () => {
    setTimeout(() => {
      performanceMonitor.monitorBundleSize();
    }, 1000);
  });
};

/**
 * Zone-specific cache configurations for TanStack Query
 * Different zones have different data freshness requirements
 * This enables optimal caching per application zone
 */
export const ZONE_CACHE_CONFIG = {
  // Consumer app: frequently updated personal data, assets, plan info
  app: {
    staleTime: 30 * 1000,        // 30 seconds - update frequently for fresh data
    gcTime: 5 * 60 * 1000,       // 5 minutes - keep in memory for quick navigation
  },
  // Partner portal: cases and audit data, moderately stable
  partner: {
    staleTime: 60 * 1000,        // 1 minute - moderate freshness for cases
    gcTime: 10 * 60 * 1000,      // 10 minutes - longer retention for audit data
  },
  // Admin dashboard: stable reference data, users, partners
  admin: {
    staleTime: 2 * 60 * 1000,    // 2 minutes - user/partner data doesn't change often
    gcTime: 15 * 60 * 1000,      // 15 minutes - retain longer, less frequent updates
  },
  // Enterprise: organization settings, members (stable)
  enterprise: {
    staleTime: 5 * 60 * 1000,    // 5 minutes - settings are stable
    gcTime: 20 * 60 * 1000,      // 20 minutes - org structures don't change frequently
  },
  // Public: marketing pages, static content
  public: {
    staleTime: 10 * 60 * 1000,   // 10 minutes - static content
    gcTime: 30 * 60 * 1000,      // 30 minutes - cache aggressive for public pages
  },
} as const;

/**
 * Get cache configuration for a specific zone
 */
export function getCacheConfigForZone(zone: keyof typeof ZONE_CACHE_CONFIG) {
  return ZONE_CACHE_CONFIG[zone] || ZONE_CACHE_CONFIG.app;
}

/**
 * Link prefetching scheduler to prevent prefetch spam
 * Debounces rapid prefetch requests when user hovers multiple links
 */
export class PrefetchScheduler {
  private pendingPrefetches: Set<string> = new Set();
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private debounceMs: number;

  constructor(debounceMs = 100) {
    this.debounceMs = debounceMs;
  }

  /**
   * Schedule a prefetch operation with debouncing
   */
  schedule(key: string, prefetchFn: () => void) {
    this.pendingPrefetches.add(key);

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.pendingPrefetches.forEach(() => {
        prefetchFn();
      });
      this.pendingPrefetches.clear();
    }, this.debounceMs);
  }

  /**
   * Cancel pending prefetches
   */
  cancel() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.pendingPrefetches.clear();
  }
}

/**
 * Report page-specific performance metrics
 * Tracks DNS, TCP, TTFB, page load, and DOM timing
 */
export function reportPagePerformanceMetrics(pageName: string) {
  if (!window.performance || !window.performance.timing) {
    return null;
  }

  const timing = window.performance.timing;
  
  const metrics = {
    page: pageName,
    timestamp: new Date().toISOString(),
    // DNS and TCP timing
    dnsTime: Math.max(0, timing.domainLookupEnd - timing.domainLookupStart),
    tcpTime: Math.max(0, timing.connectEnd - timing.connectStart),
    // Time to First Byte
    ttfb: Math.max(0, timing.responseStart - timing.navigationStart),
    // Total page load
    pageLoadTime: Math.max(0, timing.loadEventEnd - timing.navigationStart),
    // DOM readiness
    domInteractive: Math.max(0, timing.domInteractive - timing.navigationStart),
    domComplete: Math.max(0, timing.domComplete - timing.navigationStart),
    // Resource count
    resourceCount: performance.getEntriesByType('resource').length,
  };

  console.debug('[Page Performance Metrics]', metrics);
  return metrics;
}

/**
 * Helper for measuring time-to-interactive on specific routes
 */
export interface RoutePerformanceMetrics {
  routePath: string;
  initTime: number; // Time route initiated
  loadTime: number; // Total load time
  interactiveTime: number; // Time to interactive
  criticalResources: string[];
}

/**
 * Start measuring a route transition
 */
export function startRouteTimer() {
  return performance.now();
}

/**
 * End measuring a route transition and report metrics
 */
export function endRouteTimer(
  startTime: number,
  routePath: string,
  criticalResources: string[] = []
): RoutePerformanceMetrics {
  const loadTime = performance.now() - startTime;
  
  // Log slow routes
  if (loadTime > 3000) {
    captureMessage(
      `Slow route load: ${routePath} took ${loadTime.toFixed(0)}ms`,
      'warning'
    );
  }

  const metrics: RoutePerformanceMetrics = {
    routePath,
    initTime: startTime,
    loadTime,
    interactiveTime: 0, // Set by route's interactive state
    criticalResources,
  };

  console.debug('[Route Performance]', metrics);
  return metrics;
}